import React, { Children, useEffect, useMemo, useRef, useState } from 'react';
import Select, {
  ActionMeta,
  components,
  ControlProps,
  CSSObjectWithLabel,
  GroupBase,
  MultiValue,
  OptionProps,
  StylesConfig,
  ValueContainerProps,
} from 'react-select';
import { useTheme } from '@mui/material';
import CheckboxComponent from 'app/components/Checkbox/Checkbox';
import {
  CustomSelectContainer,
  CustomSelectStyle,
  InputLabelContainer,
  TextFieldArea,
} from 'app/components/CustomSelect/styles';
import { TCustomValue } from 'app/components/CustomSelect';
import { CheckboxOptionContent, SelectedCountLabel } from './styles';

const SELECT_ALL_VALUE = '__SELECT_ALL__';
const CLEAR_INDICATOR_CLASS = 'amortization-grace-clear-indicator';

const selectAllOption: TCustomValue = {
  value: SELECT_ALL_VALUE,
  label: 'Selecionar todos',
};

type AmortizationGraceMonthsSelectProps = {
  labelValue: string;
  name: string;
  options: TCustomValue[];
  value: TCustomValue[];
  placeholder?: string;
  className?: string;
  handleInputChange: (evt: {
    target: { name: string; value?: TCustomValue[] };
  }) => void;
};

const AmortizationGraceMonthsSelect = ({
  labelValue,
  name,
  options,
  value,
  placeholder = 'Selecionar',
  className,
  handleInputChange,
}: AmortizationGraceMonthsSelectProps) => {
  const theme = useTheme();
  const primaryColor = theme.palette.brand.primary.base;
  const containerRef = useRef<HTMLDivElement>(null);
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const openMenu = () => setMenuIsOpen(true);
  const closeMenu = () => setMenuIsOpen(false);
  const toggleMenu = () => setMenuIsOpen((isOpen) => !isOpen);

  const optionsWithSelectAll = useMemo(
    () => [selectAllOption, ...options],
    [options],
  );

  const allSelected = options.length > 0 && value.length === options.length;

  const displayValue = useMemo(
    () => (allSelected ? [selectAllOption, ...value] : value),
    [allSelected, value],
  );

  useEffect(() => {
    if (!menuIsOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current && !containerRef.current.contains(target)) {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [menuIsOpen]);

  const CheckboxOption = (props: OptionProps<TCustomValue, true>) => (
    <components.Option {...props}>
      <CheckboxOptionContent>
        <CheckboxComponent
          checked={props.isSelected}
          customcolor={primaryColor}
          size="small"
          tabIndex={-1}
          disableRipple
          sx={{
            padding: 0,
            pointerEvents: 'none',
            color: primaryColor,
            '&.Mui-checked': {
              color: primaryColor,
            },
          }}
        />
        <span>{props.label}</span>
      </CheckboxOptionContent>
    </components.Option>
  );

  const ValueContainer = ({
    children,
    ...props
  }: ValueContainerProps<TCustomValue, true>) => {
    const selected = props
      .getValue()
      .filter((item) => item.value !== SELECT_ALL_VALUE);
    const childrenArray = Children.toArray(children);
    const input = childrenArray[childrenArray.length - 1];

    return (
      <components.ValueContainer {...props}>
        {selected.length > 0 ? (
          <>
            <SelectedCountLabel>
              {selected.length === 1
                ? selected[0].label
                : `${selected.length} parcelas selecionadas`}
            </SelectedCountLabel>
            {input}
          </>
        ) : (
          children
        )}
      </components.ValueContainer>
    );
  };

  const Control = (props: ControlProps<TCustomValue, true>) => (
    <components.Control
      {...props}
      innerProps={{
        ...props.innerProps,
        onMouseDown: (event) => {
          if (event.button !== 0) return;

          const target = event.target as HTMLElement;
          if (target.closest(`.${CLEAR_INDICATOR_CLASS}`)) {
            props.innerProps.onMouseDown?.(event);
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          toggleMenu();
        },
      }}
    />
  );

  const handleChange = (
    selected: MultiValue<TCustomValue>,
    actionMeta: ActionMeta<TCustomValue>,
  ) => {
    if (actionMeta.action === 'clear') {
      handleInputChange({
        target: { name, value: [] },
      });
      return;
    }

    if (
      actionMeta.action === 'select-option' &&
      actionMeta.option?.value === SELECT_ALL_VALUE
    ) {
      handleInputChange({
        target: { name, value: [...options] },
      });
      return;
    }

    if (
      actionMeta.action === 'deselect-option' &&
      actionMeta.option?.value === SELECT_ALL_VALUE
    ) {
      handleInputChange({
        target: { name, value: [] },
      });
      return;
    }

    const withoutSelectAll = (selected ? [...selected] : []).filter(
      (item) => item.value !== SELECT_ALL_VALUE,
    );

    handleInputChange({
      target: {
        name,
        value: withoutSelectAll,
      },
    });
  };

  const customSelectStyle = useMemo((): StylesConfig<
    TCustomValue,
    true,
    GroupBase<TCustomValue>
  > => {
    const base = CustomSelectStyle(theme) as StylesConfig<
      TCustomValue,
      true,
      GroupBase<TCustomValue>
    >;

    return {
      ...base,
      control: (provided, state) =>
        ({
          ...(typeof base.control === 'function'
            ? base.control(provided, state)
            : provided),
          height: '2.5rem',
          minHeight: '2.5rem',
          overflow: 'visible',
          cursor: 'pointer',
        } as CSSObjectWithLabel),
      option: (provided, state) =>
        ({
          ...provided,
          backgroundColor: state.isFocused
            ? theme.palette.brand.gray[100] ?? '#F5F5F5'
            : 'white',
          color: theme.palette.text.primary,
          cursor: 'pointer',
          padding: '8px 12px',
          '&:active': {
            backgroundColor: theme.palette.brand.gray[100] ?? '#F5F5F5',
          },
        } as CSSObjectWithLabel),
      multiValue: () => ({ display: 'none' }),
      valueContainer: (provided) =>
        ({
          ...provided,
          flexWrap: 'nowrap',
          overflow: 'hidden',
          paddingTop: 0,
          paddingBottom: 0,
          cursor: 'pointer',
        } as CSSObjectWithLabel),
      input: (provided) =>
        ({
          ...provided,
          margin: 0,
          padding: 0,
          caretColor: 'transparent',
          cursor: 'pointer',
        } as CSSObjectWithLabel),
      placeholder: (provided) =>
        ({
          ...provided,
          cursor: 'pointer',
        } as CSSObjectWithLabel),
      singleValue: (provided) =>
        ({
          ...provided,
          cursor: 'pointer',
        } as CSSObjectWithLabel),
      indicatorsContainer: (provided) =>
        ({
          ...provided,
          alignSelf: 'stretch',
          height: '100%',
          flexShrink: 0,
        } as CSSObjectWithLabel),
      clearIndicator: (provided) =>
        ({
          ...provided,
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          padding: '0 8px',
          color: theme.palette.brand.gray[500] ?? '#9E9E9E',
          ':hover': {
            color: primaryColor,
          },
        } as CSSObjectWithLabel),
      dropdownIndicator: (provided) =>
        ({
          ...provided,
          display: 'flex',
          alignItems: 'center',
          padding: '0 8px',
          cursor: 'pointer',
        } as CSSObjectWithLabel),
    };
  }, [theme, primaryColor]);

  return (
    <CustomSelectContainer ref={containerRef}>
      <InputLabelContainer>{labelValue}</InputLabelContainer>
      <TextFieldArea hasLabel>
        <Select<TCustomValue, true>
          inputId={name}
          className={className}
          classNames={{
            control: () => 'select-control',
            valueContainer: () => 'value-container',
            clearIndicator: () => CLEAR_INDICATOR_CLASS,
          }}
          value={displayValue}
          options={optionsWithSelectAll}
          placeholder={placeholder}
          isMulti
          isClearable
          isSearchable={false}
          menuIsOpen={menuIsOpen}
          onMenuOpen={openMenu}
          onMenuClose={() => undefined}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          blurInputOnSelect={false}
          openMenuOnClick={false}
          openMenuOnFocus={false}
          styles={customSelectStyle}
          components={{
            Option: CheckboxOption,
            MultiValue: () => null,
            ValueContainer,
            Control,
          }}
          onChange={handleChange}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              closeMenu();
            }
          }}
        />
      </TextFieldArea>
    </CustomSelectContainer>
  );
};

export default AmortizationGraceMonthsSelect;
